import { useState, useEffect } from "react";
import { useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, MessageCircle } from "lucide-react";
import { NAV, BRAND, waLink } from "../../lib/data";

export const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const { pathname } = useLocation();
  const home = pathname === "/";
  const to = (h) => (home ? h : `/${h}`);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <motion.header
      initial={{ y: -80, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
      className="fixed top-0 inset-x-0 z-50"
      data-testid="navbar"
    >
      <div
        className={`transition-colors duration-500 ${
          scrolled
            ? "bg-white/80 backdrop-blur-xl border-b border-gray-200/70"
            : "bg-transparent"
        }`}
      >
        <div className="max-w-7xl mx-auto px-5 md:px-10 h-[74px] flex items-center justify-between">
          <a href={home ? "#beranda" : "/"} className="flex items-center gap-3 group" data-testid="logo">
            <img
              src="/logo-satcom.png"
              alt="SATCOM MEDIA TEKNOLOGI"
              className="h-9 md:h-10 w-auto"
              loading="eager"
            />
          </a>

          <nav className="hidden lg:flex items-center gap-1">
            {NAV.map((n) => (
              <a
                key={n.href}
                href={to(n.href)}
                data-testid={`nav-${n.href.slice(1)}`}
                className={`relative px-4 py-2 text-sm font-medium rounded-full transition-colors ${
                  scrolled ? "text-[#111827] hover:text-[#0B63F6]" : "text-white/80 hover:text-white"
                }`}
              >
                {n.label}
              </a>
            ))}
          </nav>

          <div className="flex items-center gap-2">
            <a
              href={waLink()}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="nav-whatsapp-cta"
              className="hidden sm:inline-flex items-center gap-2 rounded-full bg-[#0B63F6] hover:bg-[#16C7E8] text-white text-sm font-semibold px-5 py-2.5 transition-colors shadow-lg shadow-[#0B63F6]/25"
            >
              <MessageCircle className="h-4 w-4" />
              Konsultasi via WhatsApp
            </a>
            <button
              onClick={() => setOpen((v) => !v)}
              data-testid="mobile-menu-toggle"
              className={`lg:hidden grid place-items-center h-11 w-11 rounded-xl border ${
                scrolled ? "border-gray-200 text-[#071A33]" : "border-white/20 text-white"
              }`}
              aria-label="Menu"
            >
              {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
            </button>
          </div>
        </div>
      </div>

      <AnimatePresence>
        {open && (
          <motion.nav
            initial={{ opacity: 0, y: -12 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -12 }}
            className="lg:hidden mx-4 mt-2 rounded-2xl bg-white/95 backdrop-blur-xl border border-gray-200 shadow-2xl p-3"
            data-testid="mobile-menu"
          >
            {NAV.map((n) => (
              <a
                key={n.href}
                href={to(n.href)}
                onClick={() => setOpen(false)}
                className="block px-4 py-3 rounded-xl text-[#111827] font-medium hover:bg-[#F5F7FA]"
              >
                {n.label}
              </a>
            ))}
            <a
              href={waLink()}
              target="_blank"
              rel="noopener noreferrer"
              className="mt-2 flex items-center justify-center gap-2 rounded-xl bg-[#0B63F6] text-white font-semibold px-4 py-3"
            >
              <MessageCircle className="h-4 w-4" /> Konsultasi via WhatsApp
            </a>
          </motion.nav>
        )}
      </AnimatePresence>
    </motion.header>
  );
};
